import { MapPin, Camera, Monitor } from 'lucide-react';
import FadeIn from '@/components/shared/FadeIn';

const STEPS = [
  {
    number: '01',
    icon: MapPin,
    title: 'We come to you',
    description:
      'Our certified technicians arrive on-site with professional capture equipment. No setup burden on your team — we handle everything from scheduling to execution.',
  },
  {
    number: '02',
    icon: Camera,
    title: 'We capture everything',
    description:
      'Underground utilities, aerial deck captures, and full 360-degree walkthroughs — all in a single visit. Comprehensive documentation at every project milestone.',
  },
  {
    number: '03',
    icon: Monitor,
    title: 'Instant platform access',
    description:
      'Your full documentation library is live on the Ironview 360 platform within 24 hours. Access, share, and reference it from anywhere — forever.',
  },
];

const HowItWorksSection: React.FC = () => {
  return (
    <section id="how-it-works" className="relative overflow-hidden bg-maintenance-navy">
      {/* Equipment photography as full bleed background, dark overlay */}
      <img
        src="/static/images/pexels-bidvine-1249611-p-1600.jpg"
        alt="Ironview 360 camera equipment"
        className="absolute inset-0 w-full h-full object-cover object-center opacity-20"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-maintenance-navy/60 via-transparent to-maintenance-navy/80" />

      <div className="relative z-10 container mx-auto px-6 md:px-12 lg:px-16 py-28 md:py-36">
        {/* Header */}
        <FadeIn className="mb-20 md:mb-24">
          <p className="text-maintenance-cyan text-xs uppercase tracking-widest font-semibold mb-4">The Process</p>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-tight max-w-2xl">
            Simple for you.<br />
            <span className="text-maintenance-cyan">Comprehensive for everyone.</span>
          </h2>
        </FadeIn>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5">
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            return (
              <FadeIn key={step.number} delay={i * 120}>
                <div className="bg-maintenance-navy/80 backdrop-blur-sm px-8 md:px-10 py-10 md:py-12">
                  {/* Step number + icon row */}
                  <div className="flex items-center gap-4 mb-8">
                    <span className="text-5xl font-black text-white leading-none">{step.number}</span>
                    <div className="w-12 h-12 rounded-xl bg-maintenance-blue/20 flex items-center justify-center shrink-0">
                      <Icon className="h-5 w-5 text-maintenance-cyan" />
                    </div>
                  </div>

                  <h3 className="text-xl md:text-2xl font-bold text-white mb-4">{step.title}</h3>
                  <p className="text-white/50 text-sm md:text-base leading-relaxed">{step.description}</p>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
